/**
 * Valuation Dashboard Utility Functions
 * Formatting, color logic, and financial calculations
 */

import {
  METHOD_DESCRIPTIONS,
  RATIO_BENCHMARKS,
  CATEGORY_BACKGROUND_CLASSES,
  STATUS_TEXT_COLORS
} from './valuationDashboardConstants';

/**
 * Format a number as US currency
 * @param {number} value - The value to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value) => {
  if (!value) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Format large numbers with K/M suffix
 * @param {number} value - The value to format
 * @returns {string} Formatted string with K/M suffix
 */
export const formatShortCurrency = (value) => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `${(value / 1000).toFixed(0)}K`;
  }
  return value.toString();
};

/**
 * Get valuation method description
 * @param {string} method - Valuation method key
 * @returns {string} Method description
 */
export const getMethodDescription = (method) => {
  return METHOD_DESCRIPTIONS[method?.toLowerCase()] ||
    'Industry-standard valuation methodology based on your business financials.';
};

/**
 * Get color class based on ratio value and benchmarks
 * @param {string} ratioName - Name of the ratio
 * @param {number} value - Ratio value
 * @returns {string} Color class ('green', 'yellow', or 'red')
 */
export const getRatioColorClass = (ratioName, value) => {
  const benchmark = RATIO_BENCHMARKS[ratioName];
  if (!benchmark) return 'yellow'; // default to yellow if no benchmark

  const { good, acceptable, higher } = benchmark;

  if (higher) {
    // Higher is better (e.g., profitability, liquidity ratios)
    if (value >= good) return 'green';
    if (value >= acceptable) return 'yellow';
    return 'red';
  } else {
    // Lower is better (e.g., debt ratios)
    if (value <= good) return 'green';
    if (value <= acceptable) return 'yellow';
    return 'red';
  }
};

/**
 * Get background gradient CSS classes based on color and category
 * @param {string} color - Status color ('green', 'yellow', or 'red')
 * @param {string} category - Ratio category
 * @returns {string} Tailwind CSS classes
 */
export const getBackgroundClass = (color, category) => {
  return CATEGORY_BACKGROUND_CLASSES[category][color];
};

/**
 * Get text color CSS class based on status color
 * @param {string} color - Status color ('green', 'yellow', or 'red')
 * @returns {string} Tailwind CSS class
 */
export const getTextColorClass = (color) => {
  return STATUS_TEXT_COLORS[color];
};

/**
 * Helper function to get latest value from multi-year data
 * Used for advanced valuations with multi-year projections
 * @param {*} field - Field value (can be object or direct value)
 * @returns {number} Latest value
 */
const getLatestValue = (field) => {
  if (!field) return 0;
  if (typeof field === 'object' && !Array.isArray(field)) {
    // Multi-year data (advanced valuation)
    const years = Object.keys(field).sort();
    if (years.length === 0) return 0;
    const latestYear = years[years.length - 1];
    return parseFloat(field[latestYear]) || 0;
  }
  // Simple valuation or direct value
  return parseFloat(field) || 0;
};

/**
 * Calculate financial ratios from current financial data
 * @param {Object} financials - Financial data object
 * @param {Object} latestValuation - Latest valuation object (for input_data)
 * @returns {Object} Calculated financial ratios
 */
export const calculateFinancialRatios = (financials, latestValuation) => {
  const revenue = financials.revenue || 0;
  const ebitda = financials.ebitda || 0;
  const grossProfit = financials.grossProfit || 0;
  const totalAssets = financials.assetValue || 0;

  // Get current assets and liabilities from input_data if available
  let currentAssets = 0;
  let currentLiabilities = 0;
  let totalLiabilities = 0;
  let cash = 0;
  let profitAfterTax = 0;
  let equity = 0;

  try {
    const inputData = latestValuation?.input_data ?
      (typeof latestValuation.input_data === 'string' ?
        JSON.parse(latestValuation.input_data) :
        latestValuation.input_data) : null;

    if (inputData) {
      currentAssets = getLatestValue(inputData.current_assets) || totalAssets * 0.4;
      // Handle both current_liabilities (from simple) and short_term_debt (from advanced modal)
      currentLiabilities = getLatestValue(inputData.current_liabilities) ||
                          getLatestValue(inputData.short_term_debt) ||
                          totalAssets * 0.2;
      totalLiabilities = getLatestValue(inputData.total_liabilities) || totalAssets * 0.4;
      cash = getLatestValue(inputData.cash) || totalAssets * 0.1;
      profitAfterTax = getLatestValue(inputData.profit_after_tax) || 0;
      // Get equity from input_data if available, otherwise calculate from assets - liabilities
      equity = getLatestValue(inputData.equity) || (totalAssets - totalLiabilities);
    } else {
      // Use estimates if no data available
      currentAssets = totalAssets * 0.4;
      currentLiabilities = totalAssets * 0.2;
      totalLiabilities = totalAssets * 0.4;
      cash = totalAssets * 0.1;
      equity = totalAssets - totalLiabilities;
    }
  } catch (e) {
    console.error('Error parsing financial data for ratios:', e);
    // Use estimates if parsing fails
    currentAssets = totalAssets * 0.4;
    currentLiabilities = totalAssets * 0.2;
    totalLiabilities = totalAssets * 0.4;
    cash = totalAssets * 0.1;
    equity = totalAssets - totalLiabilities;
  }

  return {
    // Profitability Ratios
    grossMargin: revenue > 0 ? ((grossProfit / revenue) * 100) : 0,
    ebitdaMargin: revenue > 0 ? ((ebitda / revenue) * 100) : 0,

    // Liquidity Ratios
    currentRatio: currentLiabilities > 0 ? (currentAssets / currentLiabilities) : 0,
    cashRatio: currentLiabilities > 0 ? (cash / currentLiabilities) : 0,

    // Leverage Ratios
    debtToEquity: equity > 0 ? (totalLiabilities / equity) : 0,
    debtToAssets: totalAssets > 0 ? ((totalLiabilities / totalAssets) * 100) : 0,

    // Efficiency Ratios
    assetTurnover: totalAssets > 0 ? (revenue / totalAssets) : 0,
    // ROA and ROE should use net income (profit_after_tax), not EBITDA
    roa: totalAssets > 0 ? ((profitAfterTax / totalAssets) * 100) : 0,
    roe: equity > 0 ? ((profitAfterTax / equity) * 100) : 0,
  };
};
